(() => {
  const signupStorageKey = 'xden-signup-pending';

  const setupNaturalCarousel = (carousel) => {
    if (!carousel || carousel.dataset.xdenReady === 'true') return;
    carousel.dataset.xdenReady = 'true';

    const slides = [...carousel.querySelectorAll('[data-natural-model]')];
    const tabs = [...carousel.querySelectorAll('[data-natural-target]')];
    const count = carousel.querySelector('[data-natural-count]');
    const previous = carousel.querySelector('[data-natural-prev]');
    const next = carousel.querySelector('[data-natural-next]');
    const progress = carousel.querySelector('[data-natural-progress]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplayDelay = 4000;
    let current = 0;
    let autoplayTimer = 0;
    let autoplayPaused = false;

    const resetProgress = () => {
      if (!progress || reduceMotion || autoplayPaused) return;
      progress.classList.remove('is-running');
      void progress.offsetWidth;
      progress.classList.add('is-running');
    };

    const scheduleAutoplay = () => {
      window.clearTimeout(autoplayTimer);
      progress?.classList.remove('is-running');
      if (reduceMotion || autoplayPaused || !carousel.isConnected) return;
      resetProgress();
      autoplayTimer = window.setTimeout(() => activate(current + 1), autoplayDelay);
    };

    const activate = (nextIndex) => {
      current = (nextIndex + slides.length) % slides.length;
      const activeModel = slides[current].dataset.naturalModel;

      slides.forEach((slide, index) => {
        const active = index === current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });

      tabs.forEach((tab) => {
        const active = tab.dataset.naturalTarget === activeModel;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      if (count) count.textContent = `${current + 1} / ${slides.length}`;
      scheduleAutoplay();
    };

    tabs.forEach((tab) => tab.addEventListener('click', () => {
      activate(slides.findIndex((slide) => slide.dataset.naturalModel === tab.dataset.naturalTarget));
    }));
    previous?.addEventListener('click', () => activate(current - 1));
    next?.addEventListener('click', () => activate(current + 1));
    carousel.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') activate(current - 1);
      if (event.key === 'ArrowRight') activate(current + 1);
    });
    carousel.addEventListener('mouseenter', () => {
      autoplayPaused = true;
      scheduleAutoplay();
    });
    carousel.addEventListener('mouseleave', () => {
      autoplayPaused = false;
      scheduleAutoplay();
    });
    carousel.addEventListener('focusin', () => {
      autoplayPaused = true;
      scheduleAutoplay();
    });
    carousel.addEventListener('focusout', (event) => {
      if (carousel.contains(event.relatedTarget)) return;
      autoplayPaused = false;
      scheduleAutoplay();
    });

    let touchStartX = 0;
    let touchStartY = 0;
    carousel.addEventListener('touchstart', (event) => {
      autoplayPaused = true;
      scheduleAutoplay();
      const touch = event.changedTouches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    }, { passive: true });
    carousel.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      if (Math.abs(deltaX) >= 48 && Math.abs(deltaX) >= Math.abs(deltaY)) {
        activate(current + (deltaX < 0 ? 1 : -1));
      }
      autoplayPaused = false;
      scheduleAutoplay();
    }, { passive: true });

    activate(0);
  };

  const setupScrollCarousel = (carousel) => {
    if (!carousel || carousel.dataset.xdenReady === 'true') return;
    carousel.dataset.xdenReady = 'true';
    const track = carousel.querySelector('[data-scroll-track]');
    const controls = carousel.querySelector('[data-scroll-controls]');
    if (!track || !controls) return;

    const items = [...track.children];
    const previous = controls.querySelector('[data-scroll-prev]');
    const next = controls.querySelector('[data-scroll-next]');
    const count = controls.querySelector('[data-scroll-count]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let current = 0;
    let scrollFrame = 0;

    const itemLeft = (item) => item.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    const update = () => {
      current = items.reduce((nearest, item, index) => (
        Math.abs(itemLeft(item) - track.scrollLeft) < Math.abs(itemLeft(items[nearest]) - track.scrollLeft) ? index : nearest
      ), 0);
      if (count) count.textContent = `${current + 1} / ${items.length}`;
      if (previous) previous.disabled = current === 0;
      if (next) next.disabled = current === items.length - 1;
    };
    const goTo = (index) => {
      const target = Math.max(0, Math.min(index, items.length - 1));
      track.scrollTo({ left: itemLeft(items[target]), behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    previous?.addEventListener('click', () => goTo(current - 1));
    next?.addEventListener('click', () => goTo(current + 1));
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(current - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); goTo(current + 1); }
    });
    track.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(update);
    }, { passive: true });

    update();
  };

  const readPendingSignup = () => {
    try {
      const pending = JSON.parse(window.sessionStorage.getItem(signupStorageKey) || 'null');
      if (!pending || !pending.sectionId || Date.now() - pending.createdAt > 10 * 60 * 1000) return null;
      return pending;
    } catch (_error) {
      return null;
    }
  };

  const clearPendingSignup = () => {
    try {
      window.sessionStorage.removeItem(signupStorageKey);
    } catch (_error) {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  };

  const setupSignupTracking = (root = document) => {
    root.querySelectorAll('[data-xden-signup-form]').forEach((form) => {
      if (form.dataset.xdenSignupReady === 'true') return;
      form.dataset.xdenSignupReady = 'true';
      form.addEventListener('submit', () => {
        if (!form.checkValidity()) return;
        try {
          window.sessionStorage.setItem(signupStorageKey, JSON.stringify({
            sectionId: form.dataset.sectionId,
            createdAt: Date.now(),
          }));
        } catch (_error) {
          // The server success state and URL parameter remain as fallbacks.
        }
      });
    });
  };

  const setupSuccessModals = (root = document) => {
    const modals = [...root.querySelectorAll('[data-xden-success-modal]')];
    if (!modals.length) return;

    const parameters = new URLSearchParams(window.location.search);
    const postedByShopify = parameters.get('customer_posted') === 'true';
    const serverConfirmedModal = modals.find((modal) => modal.dataset.postedSuccessfully === 'true');
    if (!postedByShopify && !serverConfirmedModal) return;

    const pendingSignup = readPendingSignup();
    const targetId = pendingSignup?.sectionId || decodeURIComponent(window.location.hash.replace(/^#/, ''));
    const activeModal = serverConfirmedModal
      || modals.find((modal) => modal.dataset.sectionId === targetId)
      || modals[0];

    modals.forEach((modal) => {
      if (modal !== activeModal) modal.hidden = true;
    });

    if (activeModal.dataset.xdenReady === 'true') return;
    activeModal.dataset.xdenReady = 'true';
    document.body.appendChild(activeModal);
    activeModal.hidden = false;
    document.documentElement.classList.add('xden-modal-open');
    clearPendingSignup();

    if (postedByShopify && window.history?.replaceState) {
      parameters.delete('customer_posted');
      const query = parameters.toString();
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
      window.history.replaceState({}, '', nextUrl);
    }

    const closeButton = activeModal.querySelector('.xden-success-modal__close');
    const close = () => {
      activeModal.hidden = true;
      document.documentElement.classList.remove('xden-modal-open');
    };

    activeModal.querySelectorAll('[data-xden-modal-close]').forEach((control) => {
      control.addEventListener('click', close);
    });
    activeModal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
    window.requestAnimationFrame(() => closeButton?.focus());
  };

  const setupExclusiveAccordions = (root = document) => {
    root.querySelectorAll('[data-exclusive-accordion]').forEach((accordion) => {
      if (accordion.dataset.xdenAccordionReady === 'true') return;
      accordion.dataset.xdenAccordionReady = 'true';
      const items = [...accordion.querySelectorAll('details')];
      items.forEach((item) => item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((other) => {
          if (other !== item) other.open = false;
        });
      }));
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-natural-carousel]').forEach(setupNaturalCarousel);
    root.querySelectorAll('[data-scroll-carousel]').forEach(setupScrollCarousel);
    setupExclusiveAccordions(root);
    setupSignupTracking(root);
    setupSuccessModals(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
